import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../../data/site";
import { SERVICES } from "../../data/services";
import { useEnquiry } from "./EnquiryContext";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";

const navCls = ({ isActive }) =>
  `relative py-2 text-[13.5px] font-medium transition-colors ${isActive ? "text-[#0B1220]" : "text-slate-600 hover:text-[#0B1220]"}`;

const ServicesMenu = () => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 120); };

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 py-2 text-[13.5px] font-medium transition-colors ${pathname.startsWith("/services") ? "text-[#0B1220]" : "text-slate-600 hover:text-[#0B1220]"}`}
        data-testid="nav-services-trigger">
        Services <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 top-full z-50 w-[640px] -translate-x-1/2 pt-4" data-testid="nav-services-dropdown">
          <div className="grid grid-cols-[1fr_200px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_60px_-20px_rgba(11,18,32,0.25)]">
            <ul className="p-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="group flex gap-4 rounded-md p-3 transition-colors hover:bg-[#F4F6F8]" data-testid={`nav-service-${s.slug}`}>
                    <s.icon className="mt-0.5 h-5 w-5 shrink-0 text-[#2563EB]" strokeWidth={1.5} />
                    <span>
                      <span className="block text-sm font-semibold text-[#0B1220]">{s.name}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{s.items.slice(0, 2).join(" · ")}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="flex flex-col justify-between bg-[#0B1220] p-6 text-white">
              <p className="text-sm leading-relaxed text-slate-300">Five disciplines. One curated network of independent professionals.</p>
              <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-blue-300" data-testid="nav-services-all">
                All services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileNav = ({ open, setOpen, onRequest }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="right" className="w-full max-w-sm overflow-y-auto p-0" data-testid="mobile-nav-drawer">
      <div className="border-b border-slate-200 px-6 py-5"><SheetTitle className="sr-only">Navigation</SheetTitle><SheetDescription className="sr-only">Site navigation</SheetDescription><Logo testId="mobile-logo" /></div>
      <nav className="px-6 py-6" aria-label="Mobile">
        <ul className="space-y-1">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <NavLink to={l.to} end={l.to === "/"} onClick={() => setOpen(false)} className="block py-2.5 font-display text-lg font-semibold text-[#0B1220]" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>
              {l.dropdown && (
                <ul className="mb-2 ml-1 border-l border-slate-200 pl-4">
                  {SERVICES.map((s) => (
                    <li key={s.slug}><Link to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-slate-600" data-testid={`mobile-nav-service-${s.slug}`}>{s.name}</Link></li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-8 grid gap-3">
          <button type="button" className="btn-primary w-full" onClick={() => { setOpen(false); onRequest(); }} data-testid="mobile-request-expert-button">Request an Expert</button>
          <Link to="/for-experts#apply" onClick={() => setOpen(false)} className="btn-secondary w-full" data-testid="mobile-join-expert-link">Join as an Expert</Link>
        </div>
      </nav>
    </SheetContent>
  </Sheet>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 border-b bg-white/85 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 ${scrolled ? "border-slate-200 shadow-[0_1px_0_rgba(11,18,32,0.02)]" : "border-transparent"}`} data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2">Skip to content</a>
      <div className="container-x flex h-[76px] items-center justify-between gap-6">
        <Logo />
        <nav className="hidden items-center gap-5 xl:flex 2xl:gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) => l.dropdown ? <ServicesMenu key={l.to} /> : (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-5 xl:flex">
          <Link to="/for-experts#apply" className="text-[13.5px] font-semibold text-[#0B1220] underline-offset-4 transition-colors hover:text-[#2563EB]" data-testid="header-join-expert-link">Join as an Expert</Link>
          <button type="button" onClick={() => openEnquiry()} className="btn-primary h-11 px-5 text-sm" data-testid="header-request-expert-button">Request an Expert</button>
        </div>
        <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 xl:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button">
          <Menu className="h-5 w-5" />
        </button>
      </div>
      <MobileNav open={mobileOpen} setOpen={setMobileOpen} onRequest={() => openEnquiry()} />
    </header>
  );
};
